import {CachedTransform, emptyArray} from "js-vextensions";
import {JournalEntry, JournalSegment} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {CreateAccessor, GetDoc, GetDocs} from "mobx-graphlink";
import {MeID} from "./users.js";
import {DreamQuiz_QuestionType} from "./engineConfigs/@EngineConfig/@EC_DreamQuiz.js";
import {GetLiveSession_Reactive, GetLiveLocalSession_Reactive} from "../../Engine/LiveSessionManagement.js";
import {GetSessionPeriods} from "./@Shared/SessionPeriodUtils.js";
import {GetSessions} from "./sessions.js";
import {SessionPeriod} from "./@Shared/SessionPeriod.js";
import {EstimateJournalEntryEndTime, EstimateJournalEntryStartTime} from "./@Shared/JournalTimeEstimates.js";

// the timing estimates live in the pure `@Shared/EntryTimeEstimates.ts`, because the journal-import
// matcher needs the exact same "when did this night end" logic and cannot import this DB module
// (see that file's header); re-exported here so all existing call sites are unchanged
export {EstimateJournalEntryEndTime as EstimateDreamEndTime};

export const GetJournalEntry = CreateAccessor((id: string|n): JournalEntry|n=>{
	return GetDoc({}, a=>a.journalEntries.get(id!));
});
export const GetJournalEntries = CreateAccessor((userID: string|n, orderByStartTime = "asc" as null|"asc"|"desc"): JournalEntry[]=>{
	if (userID == null) return emptyArray; // if we aren't signed-in, don't even try to make request (the security-rules will make it fail)
	let result = GetDocs({
		params: {filter: {creator: userID != null && {equalTo: userID}}},
	}, a=>a.journalEntries);
	if (orderByStartTime == "asc") result = result.OrderBy(a=>EstimateJournalEntryStartTime(a));
	else if (orderByStartTime == "desc") result = result.OrderByDescending(a=>EstimateJournalEntryStartTime(a));
	return result;
});

export const GetDreamEvents = CreateAccessor((segment: JournalSegment, eventMaxWords = Number.MAX_SAFE_INTEGER)=>{
	const result = [] as string[];
	const lines = segment.shortText.split("\n");
	for (const line of lines) {
		if (!line.includes(";")) continue;
		const eventCandidates = line.split(";").map(a=>a.trim()).filter(a=>a);
		for (const eventCandidate of eventCandidates) {
			if (eventCandidate.split(" ").length > eventMaxWords) continue;
			result.push(eventCandidate);
		}
	}
	return result;
});
export const GetSegmentToDreamMap = CreateAccessor((userID: string|n)=>{
	const journalEntries = GetJournalEntries(userID);
	const result = new Map<JournalSegment, JournalEntry>();
	for (const journalEntry of journalEntries) {
		for (const segment of journalEntry.segments) {
			result.set(segment, journalEntry);
		}
	}
	return result;
});
export const GetValidQuizSegments = CreateAccessor((lucidSegmentsOnly: boolean, segmentMinEvents: number, eventMaxWords: number, questionType: DreamQuiz_QuestionType)=>{
	const journalEntries = GetJournalEntries(MeID());
	return journalEntries.SelectMany(a=>a.segments).filter(a=>{
		if (a.excludeFromIntegration) return false;
		if (lucidSegmentsOnly && !a.lucid) return false;
		const events = GetDreamEvents(a, eventMaxWords);
		if (events.length < segmentMinEvents) return false;
		if (questionType == "comesAfter" && !a.ordered) return false;
		return true;
	});
});

export function SortJournalEntries(entries: JournalEntry[], filterOutTimeless: boolean) {
	let result = entries;
	if (filterOutTimeless) {
		// only include entries that have some type of timestamp (else UI can get confusing, eg. a year-old entry with no timestamp showing)
		result = result.filter(a=>a.sleepTime != null || a.wakeTime != null /*|| a.createdAt != null*/);
	}
	return result.OrderBy(a=>a.sleepTime ?? a.wakeTime /*?? a.createdAt*/ ?? Number.MAX_VALUE);
}

/*// we have caller pass in the "now" moment, so that it can maintain some stability (ie. the "now" as of last comp render)
export const Journey_GetJournalEntriesToShow = CreateAccessor({ctx: 1}, function() {
	const uiState = this.store.main.engine.journey;
	const liveJourneySession = GetLiveLocalSession_Reactive();

	const journalEntries_raw = GetJournalEntries(MeID());
	const journalEntries_sorted = SortJournalEntries(journalEntries_raw, true);
	const lastJournalEntry = journalEntries_sorted.LastOrX();

	let journalEntryForSession: JournalEntry|n;
	/*journalEntryForSession = liveJourneySession == null ? null : journalEntries_sorted.findLast(a=>{
		if (a == null) return false;
		const sleepTimeInRange = a.sleepTime != null && a.sleepTime >= liveJourneySession.startTime && (a.sleepTime <= liveJourneySession.endTime || liveJourneySession.endTime == null);
		const wakeTimeInRange = a.wakeTime != null && a.wakeTime >= liveJourneySession.startTime && (a.wakeTime <= liveJourneySession.endTime || liveJourneySession.endTime == null);
		return sleepTimeInRange || wakeTimeInRange;
	});*#/
	if (liveJourneySession && lastJournalEntry?.wakeTime == null) {
		journalEntryForSession = lastJournalEntry;
	}

	const journalEntriesToShow = journalEntries_sorted.TakeLast(uiState.pastJournalEntriesToShow + (journalEntryForSession ? 1 : 0));

	return {lastJournalEntry, journalEntryForSession, journalEntriesToShow};
});*/

// we have caller pass in the "now" moment, so that it can maintain some stability (ie. the "now" as of last comp render)
export const Journey_GetSessionPeriodsToShow = CreateAccessor({ctx: 1}, function() {
	const uiState = this.store.main.engine.journey;
	const liveJourneySession = GetLiveLocalSession_Reactive();

	const journalEntries_raw = GetJournalEntries(MeID());
	const journalEntries_sorted = SortJournalEntries(journalEntries_raw, true);
	const sessions_inDB = GetSessions(MeID());

	const sessionPeriods = GetSessionPeriods(journalEntries_sorted, sessions_inDB, true);
	const sessionPeriodsToShow = [] as SessionPeriod[];
	// move from newest to oldest, adding each entry until doing so would result in the session-periods array containing more than X journal-entries
	for (let i = sessionPeriods.length - 1; i >= 0; i--) {
		const period = sessionPeriods[i];
		const currentEntryCount = sessionPeriodsToShow.flatMap(a=>a.journalEntries).length;
		if (currentEntryCount + period.journalEntries.length > uiState.pastJournalEntriesToShow) break;
		sessionPeriodsToShow.unshift(period);
	}

	const periodForLiveSession = liveJourneySession ? sessionPeriods.find(a=>a.sessions.Any(b=>b.startTime == liveJourneySession.coreData.startTime)) : null;
	const journalEntryForLiveSession = periodForLiveSession?.journalEntries.LastOrX();
	return {periodForLiveSession, journalEntryForLiveSession, sessionPeriodsToShow};
});